import { stringifyUrl } from 'query-string';

import {
  CountryAlpha3ToAlpha2,
  Endpoints,
  ENV_MAPBOX_API_KEY,
} from '../../constants';

export type MapboxGeocodingFeature = {
  address: string;
  bbox: number[];
  center: number[];
  context: {
    id: string;
    'short_code'?: string;
    text: string;
    wikidata: string;
  }[];
  geometry: {
    type: string;
    coordinates: [number, number];
  };
  id: string;
  'place_name': string;
  'place_type': string[];
  properties: {
    'short_code': string;
    wikidata: string;
  };
  relevance: number;
  text: string;
  type: string;
};

export type MapboxGeocodingResult = {
  attribution: string;
  type: string;
  features: MapboxGeocodingFeature[];
  query: string[];
};

export type ParsedAddress = {
  city: string;
  zipCode: string;
  streetAddress: string;
  state: string;
  country: string;
};

/**
 * Since Mapbox returns a "<key>.<random_number>"
 * pattern for the address breakdown
 * Our logic is to grab specific address breakdown
 * if a certain key is prefixed with a certain key
 *
 * If it does, we'll assign the value of provided key
 *
 * For example: Mapbox returns a "region.12345",
 * we will add the value of "region.12345" to "state"
 * in order for us to have a structure of
 * {
 *  "state": "CA"
 * }
 */
export const prefixKeysMap: {
  [key: string]: string;
} = {
  postcode: 'zipCode',
  place: 'city',
  region: 'state',
  country: 'country',
};

export const prefixTransformerMap: {
  [key: string]: (value: MapboxGeocodingFeature['context'][0]) => string | undefined;
} = {
  region: (value) => value?.short_code?.split('-')?.[1],
  country: (value) => value?.short_code?.toUpperCase(),
};

export const getAddressFromContext = (context: MapboxGeocodingFeature['context']) => (
  context.reduce((accumulator, currentValue) => {
    const contextId = currentValue?.id.split('.')[0];
    const transformerFn = prefixTransformerMap[contextId];

    if (!prefixKeysMap[contextId]) {
      return accumulator;
    }

    const key = prefixKeysMap[contextId];
    accumulator[key] = transformerFn?.(currentValue) || currentValue.text;

    return accumulator;
  }, {} as ParsedAddress & { [key: string]: string })
);

export const getAddressString = (
  streetAddress: string,
  addressFromContext: ParsedAddress & { [key: string]: string},
) => {
  let addressString = streetAddress;
  Object.keys(addressFromContext).forEach((currentValue) => {
    if (Object.values(prefixKeysMap).indexOf(currentValue) > -1) {
      addressString += `, ${addressFromContext[currentValue]}`;
    }
  });
  return addressString;
};

export const getAddress = (feature: MapboxGeocodingFeature) => {
  const addressFromContext = getAddressFromContext(feature.context);
  const streetAddress = feature.address
    ? `${feature.address} ${feature.text}`
    : feature.text;

  const addressString = getAddressString(streetAddress, addressFromContext);

  return {
    ...addressFromContext,
    streetAddress,
    addressString,
  };
};

export const getPlaceFromMapbox = async (
  searchQuery: string,
  accessToken = ENV_MAPBOX_API_KEY,
) => {
  const encodedInputValue = encodeURI(searchQuery);
  const endpoint = stringifyUrl({
    url: Endpoints.Mapbox.V5.Geocoding.replace('{{query}}', encodedInputValue),
    query: {
      access_token: accessToken,
      types: 'address',
      /*
        The value for the proximity key can either be the value 'ip'
        which tells Mapbox to use the ip sent in the request to return 
        results approximately close to the user, or an array in the format
        [long,lat] to return more accurate results to the user, but requiring
        them to consent to share their location data.
      */
      proximity: 'ip',
      // https://docs.mapbox.com/api/search/geocoding/#forward-geocoding
      // Display US/Canada addresses only
      country: [
        CountryAlpha3ToAlpha2.USA,
        CountryAlpha3ToAlpha2.CAN,
      ].join(','),
    },
  });
  const response = await fetch(endpoint);

  const body = await response.json() as MapboxGeocodingResult;
  return body;
};

export const convertAddressToMapboxGeocoding = (params: {
  streetAddress: string;
}) => {
  const mapboxGeocoding: MapboxGeocodingResult = {
    attribution: '',
    type: '',
    query: [],
    features: [{
      address: '',
      bbox: [],
      center: [],
      context: [],
      geometry: {
        coordinates: [0, 0],
        type: '',
      },
      id: '',
      place_name: '',
      place_type: [],
      properties: {
        short_code: '',
        wikidata: '',
      },
      relevance: 0,
      text: params.streetAddress || '',
      type: '',
    }],
  };

  return mapboxGeocoding;
};
